import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { HealthPackage } from "src/types/GlobalInterfaces";
import { Skeleton } from "@mui/material"; // Assuming MUI is installed
import { useNavigate } from "react-router-dom";
import { CURRENCY } from "src/lib/constans";

interface HealthPackageTabCardProps {
  data?: HealthPackage;
  skeleton?: boolean;
  id?: string;
  onClose?: () => void;
}

const HealthPackageTabCard: React.FC<HealthPackageTabCardProps> = ({
  data,
  skeleton = false,
  id = "",
  onClose,
}) => {
  const navigate = useNavigate();
  const normalizedName = data?.name.replace(/\s+/g, " ").trim();
  const headings = normalizedName?.split(" ");
  let heading1 = headings ? headings[0] : "";
  let heading2 = headings ? headings[1] : "";
  let heading3 = headings?.slice(2, headings?.length).join(" ");

  let dummyTestData = [
    "Thyroid (FT3, FT4, TSH)",
    "Complete Blood Count",
    "C-Reactive Protein (CRP)",
    "Vitamin D",
  ];

  if (skeleton) {
    return (
      <div className="flex w-full h-[140px] xl:h-[200px] overflow-hidden rounded-md border border-gray-600">
        <div className="w-[38%] flex flex-col p-2 justify-between bg-gray-200">
          <div className="flex flex-col gap-2">
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="80%" height={32} />
            <Skeleton variant="text" width="70%" height={30} />
          </div>
          <div className="flex flex-col gap-1">
            <Skeleton variant="text" width="50%" height={20} />
            <Skeleton variant="text" width="60%" height={28} />
          </div>
        </div>
        <div className="w-[62%] flex flex-col py-2 2xl:py-3 gap-2 xl:gap-3 pl-2 2xl:pl-4 bg-gray-100">
          <Skeleton variant="text" width="40%" height={24} />
          <div className="flex flex-col gap-2">
            {[...Array(4)].map((_, index) => (
              <div key={index} className="flex items-center gap-2">
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="text" width="80%" height={20} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      id={id}
      className="flex w-full h-[140px] lg:h-[13vw] 2xl:h-[200px] overflow-hidden rounded-md border border-gray-600 cursor-pointer"
      onClick={() => {
        onClose?.();
        navigate(`/diagnostic-product/package/${data?._id || ""}/view`);
      }}
    >
      <div className="w-[40%] flex flex-col p-2 justify-between theme-gradient-green-bottom text-white">
        <div className="flex flex-col">
          {heading1 && (
            <p className="font-size-20px font-Lexend-Regular leading-tight">{heading1}</p>
          )}
          {heading2 && (
            <p className="font-size-24px font-Lexend-Medium leading-tight">{heading2}</p>
          )}
          {heading3 && (
            <p className="font-size-24px leading-tight font-Brush-Script-Std line-clamp-2">
              {heading3.split(" ").slice(0, 2).join(" ")}
            </p>
          )}
        </div>
        <div>
          {data?.regularPrice && data?.salePrice && (
            <p className="font-size-16px font-Lexend-Regular line-through">
              {CURRENCY} {data?.regularPrice.toLocaleString()}
            </p>
          )}
          {(data?.salePrice || data?.regularPrice) && (
            <p className="font-size-23px font-Lexend-Medium">
              {CURRENCY} {data?.salePrice.toLocaleString() || data?.regularPrice.toLocaleString()}
            </p>
          )}
        </div>
      </div>
      <div className="w-[60%] flex flex-col py-1.5 2xl:py-2 gap-1 xl:gap-2 pl-1.5 2xl:pl-3">
        <h2 className="font-size-22px font-Lexend-Regular text-[#3B3B3B]">
          {data?.noOfTests} Test{data && data?.noOfTests > 1 && "s"}
        </h2>
        <div className="flex flex-col gap-1 lg:gap-[0.3vw] justify-center">
          {data?.testDetails?.slice(0, 4).map((item: string, index: number) => (
            <div className="flex items-center gap-1 lg:gap-[0.3vw]" key={index}>
              <div className="min-w-[14px] w-fit lg:min-w-[1vw] aspect-square  rounded-full text-white theme-bg-green-dark flex items-center justify-center">
                <FontAwesomeIcon
                  icon={faCheck}
                  className="font-size-10px"
                />
              </div>{" "}
              <p className="font-size-16px font-Poppins-Regular line-clamp-1 text-[#3B3B3B]">
                {item}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HealthPackageTabCard;
